import {CodeAndExchange, Securities, securityEquality, securityMatcher} from "../../instruments/types/CodeAndExchange";
import {SecurityMap, SecurityMapOf} from "../../security/common/map/SecurityMap";
import {NewsHeadlineRecord, NewsHeadlineRecords} from "./NewsHeadlineRecord";
import {QuoteViewModel, QuoteViewModels} from "../../quote/models/QuoteViewModel";
import {Predicate, Transform} from "../../types/GenericTypes";
import moment from "moment";

export interface NewsHeadlineIndicatorModel extends CodeAndExchange {
    readonly type: HeadlineType;
}

export enum HeadlineType {
    NONE,
    MARKET_SENSITIVE,
    NEWS
}

export function NoNews(security: CodeAndExchange): NewsHeadlineIndicatorModel {
    return {
        ...security,
        type: HeadlineType.NONE
    };
}

export function MarketSensitiveNews(security: CodeAndExchange): NewsHeadlineIndicatorModel {
    return {
        ...security,
        type: HeadlineType.MARKET_SENSITIVE
    };
}

export function HasNews(security: CodeAndExchange): NewsHeadlineIndicatorModel {
    return {
        ...security,
        type: HeadlineType.NEWS
    };
}

export function buildHeadlineModels(securities: Securities,
                                    headlines: SecurityMap<NewsHeadlineRecords>,
                                    quotes: QuoteViewModels): SecurityMap<NewsHeadlineIndicatorModel> {
    const result = SecurityMapOf<NewsHeadlineIndicatorModel>();
    securities.map(modelBuilder(headlines, quotes)).forEach((m) => result.set(m, m));
    return result;
}

function modelBuilder(headlines: SecurityMap<NewsHeadlineRecords>,
                      quotes: QuoteViewModels): Transform<CodeAndExchange, NewsHeadlineIndicatorModel> {
    return (security) => {
        const securityHeadlines = headlines.getOrDefault(security, []);
        if (securityHeadlines.length === 0) {
            return NoNews(security);
        }
        const quote = quotes.find(securityEquality(security));
        if (quote == null) {
            return NoNews(security);
        }
        const todayHeadlines = securityHeadlines.filter(isHeadlineForToday(quote));
        if (todayHeadlines.length === 0) {
            return NoNews(security);
        }
        const hasSensitive = todayHeadlines.some((h) => h.marketSensitive);

        if (hasSensitive) {
            return MarketSensitiveNews(security);
        } else {
            return HasNews(security);
        }
    };
}

function isHeadlineForToday(quote: QuoteViewModel): Predicate<NewsHeadlineRecord> {
    const quoteAsAt = moment(quote.asAt);
    return (headline) => quoteAsAt.isSame(headline.at, "date");
}

export function isSameIndicator(a: NewsHeadlineIndicatorModel, b: NewsHeadlineIndicatorModel): boolean {
    return securityMatcher(a, b) && (a.type === b.type);
}
